'use client';

import { useRouter } from 'next/navigation';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';

interface BackButtonProps {
  className?: string;
  fallbackUrl?: string;
}

// Simple navigation history tracker
const NAVIGATION_HISTORY_KEY = 'starky-nav-history';

function getNavigationHistory(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const stored = sessionStorage.getItem(NAVIGATION_HISTORY_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

export function BackButton({ className, fallbackUrl = '/' }: BackButtonProps) {
  const router = useRouter();
  const [previousUrl, setPreviousUrl] = useState<string | null>(null);
  const t = useTranslations('backButton');

  useEffect(() => {
    // Get the current path
    const currentPath = window.location.pathname + window.location.search + window.location.hash;

    // Get navigation history and find the previous page
    const history = getNavigationHistory();

    // Find the most recent page that's different from current
    // NOTE: I cannot believe I'm doing this, may God help us all, web dev truly is something else
    for (let i = history.length - 1; i >= 0; i--) {
      const historyPath = history[i];
      if (historyPath && historyPath !== currentPath) {
        setPreviousUrl(historyPath);
        break;
      }
    }
  }, []);

  const handleBack = () => {
    if (previousUrl && previousUrl !== window.location.pathname) {
      // Navigate to the previous URL we stored
      router.push(previousUrl);
    } else {
      // Fallback to home page
      router.push(fallbackUrl);
    }
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleBack}
      className={`flex items-center gap-2 ${className || ''}`}
      aria-label={t('goBack')}
    >
      <ArrowLeft className="h-4 w-4" />
      <span className="hidden sm:inline">{t('back')}</span>
    </Button>
  );
}